import React, { useMemo } from 'react';
import { Button, Modal, Typography } from '@attentive-platform/stem-ui';

import { ConfirmLinkModalStyles as S } from './styles';
import { SOP_MODE_LABELS } from '../../../Constants/sopMode';
import { formatLocaleDate } from '../../../Utils/HelperFunctions';
import { resolveKamPerson, scopeRecordHasKam } from '../../ScopeConsole/api';

const EFFECTS = [
  'The company moves off free-form onto this scope record.',
  'New takeoffs render scope cards from this record and pin a snapshot at submit.',
  'The enterprise-conversion gate is satisfied.'
];

const REPLACE_EFFECTS = [
  'This submission replaces the currently linked scope record.',
  'New takeoffs render scope cards from the new record and pin a snapshot at submit.',
  'The company stays on Rosetta — it does not drop back to free-form.'
];

const summarizeTrades = record => {
  const trades = Array.isArray(record?.trades_in_scope) ? record.trades_in_scope : [];
  return trades
    .map(t => {
      const sections = (Array.isArray(t.sections) ? t.sections : [])
        .map(s => ({
          id: s.section_id || s.id,
          name: s.section_name || s.name || '—',
          scopeCount: (s.assigned_scopes || s.scopes || []).length
        }))
        .filter(s => s.scopeCount > 0);
      return {
        id: t.trade_id || t.id,
        name: t.trade_name || t.name || '—',
        sections
      };
    })
    .filter(t => t.sections.length > 0);
};

const ConfirmLinkModal = ({
  show,
  onClose,
  onConfirm,
  submission,
  company,
  submitting,
  loadingScope,
  replacing = false
}) => {
  const picked = submission?.company || {};
  const scopeMode = submission?.scope_mode;
  const scopeRecord = submission?.scopeRecord;
  const trades = useMemo(() => summarizeTrades(scopeRecord), [scopeRecord]);
  const sectionCount = trades.reduce((n, t) => n + t.sections.length, 0);
  // KAM comes from the scope-record GET (`kam: { id, name, email }`), not company_scopes.
  const kamPerson = resolveKamPerson(scopeRecord);
  const hasKam = !loadingScope && scopeRecordHasKam(scopeRecord);
  const kamLabel = loadingScope
    ? 'Loading…'
    : kamPerson?.name || kamPerson?.email || (hasKam ? 'Assigned' : 'Missing');

  if (!submission) return null;

  const facts = [
    { label: 'Company', value: picked.name || company || '—' },
    {
      label: 'Customer email',
      value: picked.customer_email || submission.customer_email || '—'
    },
    { label: 'Card id', value: submission.card_id || '—' },
    { label: 'Registered', value: formatLocaleDate(picked.registered_on) || '—' },
    { label: 'Current scope mode', value: SOP_MODE_LABELS[scopeMode] || scopeMode || '—' },
    { label: 'Key Account Manager', value: kamLabel },
    {
      label: 'Trades',
      value: loadingScope
        ? 'Loading…'
        : trades.length
          ? `${trades.length} trade${trades.length === 1 ? '' : 's'}`
          : '—'
    },
    {
      label: 'Sections',
      value: loadingScope
        ? 'Loading…'
        : sectionCount
          ? `${sectionCount} section${sectionCount === 1 ? '' : 's'}`
          : '—'
    }
  ];

  return (
    <Modal
      show={show}
      onClose={onClose}
      heading={<span className='pl-4'>Link scope record</span>}
      content={
        <S.ContentWrap>
          <S.Heading>
            {replacing
              ? `Replace ${company || 'the company'}'s current scope of record with this submission`
              : `This submission becomes ${company || 'the company'}'s scope of record`}
          </S.Heading>
          <S.FactsBox>
            {facts.map(f => (
              <S.FactRow key={f.label}>
                <Typography variant='body4' color='#808080'>
                  {f.label}
                </Typography>
                <Typography variant='body4' sx={{ fontWeight: 500, textAlign: 'right' }}>
                  {f.value}
                </Typography>
              </S.FactRow>
            ))}
          </S.FactsBox>
          {!loadingScope && trades.length > 0 && (
            <S.FactsBox>
              {trades.map(t => (
                <S.FactRow key={t.id || t.name}>
                  <Typography variant='body4' sx={{ fontWeight: 500 }}>
                    {t.name}
                  </Typography>
                  <Typography variant='body4' color='#808080' sx={{ textAlign: 'right' }}>
                    {t.sections.length
                      ? t.sections.map(s => s.name).join(', ')
                      : 'No sections'}
                  </Typography>
                </S.FactRow>
              ))}
            </S.FactsBox>
          )}
          <S.Effects>
            {(replacing ? REPLACE_EFFECTS : EFFECTS).map(text => (
              <S.BulletRow key={text}>
                <S.BulletDot />
                <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.55 }}>
                  {text}
                </Typography>
              </S.BulletRow>
            ))}
          </S.Effects>
          {!loadingScope && !hasKam && (
            <Typography variant='body4' color='#B42318' sx={{ lineHeight: 1.45 }}>
              This scope record has no Key Account Manager. Assign a KAM on the scope record before
              linking.
            </Typography>
          )}
          <Typography variant='body4' color='#808080'>
            Recorded in the activity log with your name.
          </Typography>
          <S.Actions>
            <Button variant='outlined' color='primary' size='medium' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='medium'
              loading={submitting}
              disabled={loadingScope || !hasKam}
              onClick={onConfirm}>
              {replacing ? 'Replace with this record' : 'Link scope record'}
            </Button>
          </S.Actions>
        </S.ContentWrap>
      }
    />
  );
};

export default ConfirmLinkModal;
